import React, {
    useContext,
    useEffect,
    useState,
    useMemo,
    useRef,
    useCallback,
} from 'react'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Label,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
    Brush,
    Scatter,
    ComposedChart,
    Customized,
} from 'recharts'
import dayjs from 'dayjs'

import { WidgetDataContext } from '../../../wrappers/WidgetDataContext'

import { formatNumber } from '../../../../utils/numberFormatting'
import { ColorContext } from '../../../wrappers/ColorContext'
import withConfig from '../../../wrappers/withConfig'
import CustomLegend from './CustomLegend'
import { getMultiSeriesData } from './utils'
import { GlobalTooltip } from './CustomScatterTooltip'

const defaultMaxTicks = 150

const getDomainLimitedData = (startValue, endValue, valueKey, data) =>
    data.filter((f) => f[valueKey] >= startValue && f[valueKey] <= endValue)

const calculateDomains = (data, xKey, yKey) => {
    if (!data || data.length === 0) {
        return { yDomain: [0, 0], xDomain: [0, 0] }
    }

    const yValues = data.map((item) => item[yKey])
    const minY = Math.min(...yValues)
    const maxY = Math.max(...yValues)

    const xValues = data.map((item) => item[xKey])
    const minX = Math.min(...xValues)
    const maxX = Math.max(...xValues)

    const yPadding = 0 //Math.ceil((maxY - minY) * 0.03)
    const xPadding = 0 //Math.ceil((maxX - minX) * 0.03)

    const yDomainMin = Math.max(0, minY - yPadding)
    const yDomainMax = maxY + yPadding

    return {
        yDomain: [yDomainMin, yDomainMax],
        xDomain: [minX - xPadding, maxX + xPadding],
    }
}

const MultiSeriesLineChart = withConfig(({ config }) => {
    const { widgetData: widget, showLegend } = useContext(WidgetDataContext)
    const { colorMap, updateColorMap, getAssociatedColor } =
        useContext(ColorContext)
    const chartConfig =
        config && config['CHART'] && config['CHART'][widget.DashboardName]
    const maxTicks =
        chartConfig && chartConfig['MULTI_SESIES_MAX_TICKS']
            ? chartConfig['MULTI_SESIES_MAX_TICKS']
            : defaultMaxTicks
    const data = useMemo(() => (widget ? widget.Data : null), [widget])

    const options = useMemo(
        () =>
            widget && widget.WidgetOptions
                ? JSON.parse(widget.WidgetOptions)
                : null,
        [widget]
    )

    //example of options '{ "layout": "vertical", "groupKeyName": "Date", "yKey": "Sample Depth (m)", "xKey": "Temperature (°C)", "tooltipLabels": ["Sample Date", "Sample Depth (m)", "Temperature (°C)"], "hiddenColumns": ["Date"]}'
    if (!options) {
        return null
    }

    const {
        layout = 'horizontal',
        groupKeyName,
        xKey,
        yKey,
        tooltipLabels,
    } = options

    const formattedData = useMemo(
        () =>
            data
                .filter((d) => !!d[groupKeyName]) // remove data w/out key
                .map((d) => {
                    let currentObj = d
                    for (let prop in d) {
                        //if there's any date in the object format it
                        if (prop === groupKeyName) {
                            currentObj[prop] = dayjs(d[prop]).format(
                                'MM/DD/YYYY'
                            )
                        }
                    }
                    return currentObj
                }), // transform dates to MM/DD/YYYY format
        [data, groupKeyName]
    )

    const yKeysOnFormattedData = useMemo(
        () => formattedData.map((x) => x[yKey]).sort((a, b) => a - b),
        [formattedData]
    )

    const [chartData, setChartData] = useState(formattedData)

    const uniqueGroups = useMemo(
        () => [...new Set(chartData.map((x) => x[groupKeyName]))].sort(),
        [chartData]
    )

    // create state to track hidden prop for each individual line
    const [hiddenState, setHiddenState] = useState(
        uniqueGroups.reduce((acc, curr) => ({ ...acc, [curr]: false }), {})
    )
    const [bold, setBold] = useState(null)

    useEffect(() => {
        updateColorMap(groupKeyName, uniqueGroups)
    }, [uniqueGroups, groupKeyName])

    const legend = useMemo(
        () =>
            uniqueGroups
                .map((groupName, idx) => ({
                    color: !hiddenState[groupName]
                        ? getAssociatedColor(groupName, groupKeyName)
                        : 'rgb(204, 204, 204)',
                    type: 'line',
                    dataKey: groupName,
                    value: groupName,
                    valueAsUnix: dayjs(groupName).isValid()
                        ? dayjs(groupName).toDate().getTime()
                        : groupName,
                    hide: hiddenState[groupName] ? true : false,
                }))
                .sort((a, b) => a.valueAsUnix - b.valueAsUnix),
        [hiddenState, colorMap, uniqueGroups]
    )

    useEffect(() => {
        if (showLegend === 'all' && Object.values(hiddenState).includes(true)) {
            setHiddenState(
                uniqueGroups.reduce(
                    (acc, curr) => ({ ...acc, [curr]: false }),
                    {}
                )
            )
        }
        if (
            showLegend === 'none' &&
            Object.values(hiddenState).includes(false)
        ) {
            setHiddenState(
                uniqueGroups.reduce(
                    (acc, curr) => ({ ...acc, [curr]: true }),
                    {}
                )
            )
        }
    }, [showLegend])

    const colors = useMemo(
        () =>
            uniqueGroups.reduce((acc, curr, idx) => {
                return {
                    ...acc,
                    [curr]: getAssociatedColor(curr, groupKeyName),
                }
            }, {}),
        [uniqueGroups, groupKeyName, colorMap]
    )

    const updateHiddenState = (e) => {
        setHiddenState((previousState) => ({
            ...previousState,
            [e.dataKey]: !previousState[e.dataKey],
        }))
    }

    const dataExceedsMaxTicks = useMemo(
        () => formattedData && formattedData.length > maxTicks,
        [formattedData]
    )

    const [domains, setDomains] = useState(
        calculateDomains(chartData, xKey, yKey)
    )

    const [initialBrushSet, setInitialBrushSet] = useState(false)
    useEffect(() => {
        if (dataExceedsMaxTicks) {
            setInitialBrushSet(true)
            const startIndexInY = yKeysOnFormattedData[0]
            const endIndexInY = yKeysOnFormattedData[maxTicks]

            const newData = getDomainLimitedData(
                startIndexInY,
                endIndexInY,
                yKey,
                formattedData
            )
            setChartData(newData)
        }
    }, [formattedData, yKeysOnFormattedData, dataExceedsMaxTicks, maxTicks])

    const handleOnChangeBrush = useCallback(
        (event) => {
            setInitialBrushSet(false)
            const startIndexInY = yKeysOnFormattedData[event.startIndex]
            const endIndexInY = yKeysOnFormattedData[event.endIndex]

            const newData = getDomainLimitedData(
                startIndexInY,
                endIndexInY,
                yKey,
                formattedData
            )
            setChartData(newData)
        },
        [formattedData, yKeysOnFormattedData, xKey, yKey]
    )

    //if chartData Changes also change domains
    useEffect(() => {
        setDomains(calculateDomains(chartData, xKey, yKey))
    }, [chartData, setDomains, xKey, yKey])

    const series = useMemo(() => {
        return getMultiSeriesData(
            chartData,
            uniqueGroups,
            groupKeyName,
            xKey,
            yKey
        )
    }, [chartData, uniqueGroups, options])

    const [hoveredPoint, setHoveredPoint] = useState(null)
    const Lines = useMemo(
        () =>
            series.map((s, idx) => (
                <>
                    <Line
                        type="monotone"
                        dot={false}
                        dataKey={`${xKey}`}
                        data={s.data}
                        name={s.name}
                        stroke={colors[s.name]}
                        strokeWidth={bold === s.name ? 3 : 1}
                        hide={hiddenState[s.name]}
                        connectNulls={true}
                    />
                    <Scatter
                        key={`scatter-${s.name}-${idx}`}
                        data={s.data}
                        name={`${s.name}`}
                        fill={colors[s.name]}
                        shape={(props) => {
                            const pointId = `${s.name}-${props.payload[xKey]}-${props.payload[yKey]}`
                            return (
                                <circle
                                    cx={props.cx}
                                    cy={props.cy}
                                    r={bold === s.name ? 4 : 3}
                                    className="circle"
                                    fill={colors[s.name]}
                                    stroke="#fff"
                                    strokeWidth={bold === s.name ? 2 : 1}
                                    onMouseEnter={(e) => {
                                        setHoveredPoint({
                                            ...props,
                                            pointId: pointId,
                                            seriesName: s.name,
                                            color: colors[s.name],
                                        })
                                    }}
                                    onMouseLeave={() => {
                                        setHoveredPoint(null)
                                    }}
                                    style={{
                                        cursor: 'pointer',
                                        // transition: 'r 0.2s ease',
                                    }}
                                />
                            )
                        }}
                        hide={hiddenState[s.name]}
                    />
                </>
            )),
        [uniqueGroups, hiddenState, colors, bold]
    )

    return (
        <ResponsiveContainer height={'99%'} debounce={1}>
            <ComposedChart
                data={yKeysOnFormattedData} //this drives the values on the Brush
                layout={layout}
                margin={{
                    top: 50,
                    right: 30,
                    left: 10,
                    bottom: 0,
                }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    type="number"
                    domain={domains.xDomain}
                    orientation={layout === 'vertical' ? 'top' : 'bottom'}
                    offset={50}
                    dataKey={xKey}
                    tickCount={12}
                >
                    <Label
                        value={xKey}
                        offset={50}
                        position="top"
                        className="barChartXAxisLabel"
                    />
                </XAxis>
                <YAxis
                    type="number"
                    dataKey={yKey}
                    tickFormatter={(value) => formatNumber(value, '2Decimals')}
                    domain={domains.yDomain}
                    allowDataOverflow={true}
                    tickCount={12}
                >
                    <Label
                        value={yKey}
                        angle={-90}
                        offset={20}
                        position="insideLeft"
                        className="barChartYAxisLabel"
                    />
                </YAxis>
                <Legend
                    content={
                        <CustomLegend
                            legendPayload={legend}
                            bold={bold}
                            setBold={setBold}
                            hiddenState={hiddenState}
                            updateHiddenState={updateHiddenState}
                        />
                    }
                    wrapperStyle={{
                        display: showLegend
                            ? Object.keys(legend).length
                                ? 'block'
                                : 'none'
                            : 'none',
                        // top: 1,
                        left: 0,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        alignItems: 'center',
                        width: '100%',
                    }}
                />
                <Tooltip
                    content={
                        <GlobalTooltip
                            hoveredPoint={hoveredPoint}
                            tooltipLabels={tooltipLabels}
                        />
                    }
                />
                {Lines}
                {/* {Scatters} */}
                {/* {References} */}
                {dataExceedsMaxTicks && (
                    <Brush
                        height={20}
                        y={0} // Position from top
                        {...(initialBrushSet ? { endIndex: maxTicks } : {})}
                        onChange={handleOnChangeBrush}
                        tickFormatter={(idx) =>
                            `${formatNumber(
                                yKeysOnFormattedData[idx],
                                '2Decimals'
                            )} (m)`
                        }
                    />
                )}
            </ComposedChart>
        </ResponsiveContainer>
    )
})

export default MultiSeriesLineChart
